import { useEffect, useState } from 'react';
import { CalendarPlus, Clock3, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { stripUids, withUids } from '../lib/listKeys';

const DAYS = [
  ['monday', 'Monday'], ['tuesday', 'Tuesday'], ['wednesday', 'Wednesday'],
  ['thursday', 'Thursday'], ['friday', 'Friday'], ['saturday', 'Saturday'], ['sunday', 'Sunday'],
];

const blankHours = () => ({
  timezone: 'Africa/Accra',
  weekly: Object.fromEntries(DAYS.map(([day]) => [day, { closed: true, opens: '', closes: '' }])),
  holidays: [],
});

export const PortalOpeningHoursDialog = ({ open, onOpenChange, pharmacyId, branch, onSaved }) => {
  const [value, setValue] = useState(blankHours());
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    const next = branch?.opening_hours ? JSON.parse(JSON.stringify(branch.opening_hours)) : blankHours();
    setValue({ ...next, holidays: withUids(next.holidays) });
  }, [open, branch]);

  const updateDay = (day, patch) => setValue(current => ({ ...current,
    weekly: { ...current.weekly, [day]: { ...current.weekly[day], ...patch } },
  }));
  const updateHoliday = (index, patch) => setValue(current => ({ ...current,
    holidays: current.holidays.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
  }));
  const save = async () => {
    setSaving(true);
    try {
      const response = await api.put(`/portal-links/${pharmacyId}/branches/${branch.tenant_id}/hours`, { ...value, holidays: stripUids(value.holidays) });
      toast.success('Opening hours saved');
      onSaved(response.data);
      onOpenChange(false);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Check the opening hours and try again.');
    } finally { setSaving(false); }
  };

  return <Dialog open={open} onOpenChange={next => { if (!saving) onOpenChange(next); }}>
    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="portal-hours-dialog">
      <DialogHeader><DialogTitle className="flex items-center gap-2"><Clock3 className="w-5 h-5" />Opening hours · {branch?.name}</DialogTitle>
        <DialogDescription>Set regular weekly times and date-specific holiday exceptions.</DialogDescription></DialogHeader>
      <div className="space-y-2" data-testid="portal-hours-weekly-editor">
        {DAYS.map(([day, label]) => {
          const hours = value.weekly[day];
          return <div key={day} className="grid grid-cols-[90px_1fr] sm:grid-cols-[110px_100px_1fr_1fr] gap-2 items-center border-b py-2">
            <span className="text-sm font-medium">{label}</span>
            <Label className="flex items-center gap-2 text-xs"><Checkbox checked={hours.closed}
              data-testid={`portal-hours-${day}-closed`} onCheckedChange={checked => updateDay(day, { closed: !!checked, opens: checked ? '' : '09:00', closes: checked ? '' : '17:00' })} />Closed</Label>
            {!hours.closed && <><Input type="time" value={hours.opens} data-testid={`portal-hours-${day}-opens`} onChange={event => updateDay(day, { opens: event.target.value })} />
              <Input type="time" value={hours.closes} data-testid={`portal-hours-${day}-closes`} onChange={event => updateDay(day, { closes: event.target.value })} /></>}
          </div>;
        })}
      </div>
      <div className="border-t pt-4 space-y-3" data-testid="portal-holiday-exceptions-editor">
        <div className="flex items-center justify-between gap-3"><div><h3 className="font-medium">Holiday exceptions</h3><p className="text-xs text-muted-foreground">These dates override the weekly schedule.</p></div>
          <Button type="button" size="sm" variant="outline" data-testid="portal-holiday-add" onClick={() => setValue(current => ({ ...current,
            holidays: [...current.holidays, { uid: crypto.randomUUID(), date: '', label: '', closed: true, opens: '', closes: '' }],
          }))}><CalendarPlus className="w-4 h-4" />Add date</Button></div>
        {value.holidays.map((item, index) => <div key={item.uid} className="grid sm:grid-cols-[145px_1fr_auto_auto] gap-2 items-center border-b pb-3" data-testid={`portal-holiday-row-${index}`}>
          <Input type="date" value={item.date} data-testid={`portal-holiday-date-${index}`} onChange={event => updateHoliday(index, { date: event.target.value })} />
          <Input value={item.label} maxLength={80} placeholder="Holiday name" data-testid={`portal-holiday-label-${index}`} onChange={event => updateHoliday(index, { label: event.target.value })} />
          <Label className="flex items-center gap-2 text-xs"><Checkbox checked={item.closed} data-testid={`portal-holiday-closed-${index}`}
            onCheckedChange={checked => updateHoliday(index, { closed: !!checked, opens: checked ? '' : '09:00', closes: checked ? '' : '17:00' })} />Closed</Label>
          <Button type="button" size="icon" variant="ghost" title="Remove holiday exception" data-testid={`portal-holiday-remove-${index}`}
            onClick={() => setValue(current => ({ ...current, holidays: current.holidays.filter((_, itemIndex) => itemIndex !== index) }))}><Trash2 className="w-4 h-4" /></Button>
          {!item.closed && <div className="sm:col-start-2 sm:col-span-2 grid grid-cols-2 gap-2"><Input type="time" value={item.opens} data-testid={`portal-holiday-opens-${index}`} onChange={event => updateHoliday(index, { opens: event.target.value })} />
            <Input type="time" value={item.closes} data-testid={`portal-holiday-closes-${index}`} onChange={event => updateHoliday(index, { closes: event.target.value })} /></div>}
        </div>)}
        {!value.holidays.length && <p className="text-sm text-muted-foreground" data-testid="portal-holiday-empty">No holiday exceptions added.</p>}
      </div>
      <div className="flex flex-wrap justify-end gap-2"><Button variant="outline" disabled={saving} data-testid="portal-hours-cancel" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button disabled={saving} data-testid="portal-hours-save" onClick={save}>{saving && <Loader2 className="w-4 h-4 animate-spin" />}Save hours</Button></div>
    </DialogContent>
  </Dialog>;
};